import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import Title from '../../../components/Title';
import { NavLink } from 'react-router-dom';
import { Lend } from '../../../models/lend';
import { Collect } from '../../../models/collect';
import { Office } from '../../../models/office';
import useAxiosInterceptor from '../../AxiosInterceptor';
import PaysModalWeb from './modal/pays';
import UpdateDateModal from './modal/updateDate';
import ModalClientHistory from '../../informes/modals/ModalClientHistory';
import { useSelection } from '../../../components/context/SelectionContext';
import { useTheme, useMediaQuery, Tooltip, CardActions, Alert, Switch, Typography, Divider, TextField, Box, Card, Modal, Paper, Grid, FormControl, MenuItem, InputLabel, Select, CardContent } from '@mui/material';
import { Add as AddIcon, AddAlert as AddAlertIcon, Edit as EditIcon, Preview as PreviewIcon, EditLocationAlt as EditLocationAltIcon, PersonAddAlt as PersonAddAltIcon, Delete as DeleteIcon, Paid as PaidIcon, Assessment as AssessmentIcon, DateRange as DateRangeIcon, History as HistoryIcon } from '@mui/icons-material';
import RefinanceModal from './modal/refinance';
import CollectByOfficeForm from '../../../components/filter/collectbyOffice';
import HeaderResponsive from '../../../components/layouts/HeaderResponsive';
import StickyListToolbar, {
  stickyListRegionWidth,
} from '../../../components/layouts/StickyListToolbar';
import EmptyStateMessage from '../../../components/layouts/EmptyStateMessage';
import { useDrawer } from '../../../components/context/DrawerContext';
import ConstantModal from './modal/constant';
import SimpleCustomTable from '../../../components/table/SimpleCustomTable';
import { SimpleColumn, SimpleRow, SimpleColumnGroup } from '../../../components/table/interface';
import {
  iconActionColumnSizing,
  simpleTableIconButtonSx,
} from '../../../components/table/iconActionColumnSizing';
import PermissionButton from '../../../components/common/PermissionButton';
import { useResourcePermissions } from '../../../hooks/useResourcePermissions';
import { formatCurrency } from '../../../utils/format/currency';


interface ShowAlerts {
  showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
  showLoading: () => void;
  hideLoading: () => void;
}

type RowId = string | number; // 👈 acepta string o number
const TOTAL_ROW_ID = -1;

const Abonos: React.FC<ShowAlerts> = ({ showAlert, showLoading, hideLoading }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [lends, setLends] = useState<Lend[]>([]);
  const [collectsOffice, setCollectsOffice] = useState<Collect[]>([]);
  const [offices, setOffices] = useState<Office[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [saldoRestante, setSaldoRestante] = useState(0);
  const [isModalUpdateOpen, setIsModalUpdateOpen] = useState(false);
  const [isModalRefinanceOpen, setIsModalRefinaOpen] = useState(false);
  const [selectedRefinanceClient, setSelecRefinanClient] = useState<Lend | null>(null);
  const [selectedClient, setSelectedClient] = useState<Lend | null>(null);
  const [selectedHistoryClient, setSelectedHistoryClient] = useState<any | null>(null);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [dateClient, setDateClient] = useState<Lend | null>(null);
  const { office_id, setOffice_id, collect_id, setCollect_id, user_id, setUser_id } = useSelection();
  const [refresh, setRefresh] = useState(false);
  const { drawerOpen } = useDrawer();
  const [selectedIds, setSelectedIds] = useState<RowId[]>([]);
  const [isModalConstantOpen, setIsModalConstantOpen] = useState(false);
  const { canCreate: canCreateClient } = useResourcePermissions('/administrar/clientes');
  const {
    canCreate,
    canDelete,
    canShowLocation,
    canAllowDiscount,
    canAllowManualDiscount,
    canAllowAutomaticDiscount,
    canPay,
    canRefinance,
    canUpdateConstant,
    canDeleteSelected,
    canPdf,
  } = useResourcePermissions('/prestamos/abonos/list');

  useAxiosInterceptor();
  /** Evita aplicar datos de una petición obsoleta si cambió oficina/cobro antes de que respondiera. */
  const loadSeqRef = useRef(0);

  const [modalData, setModalData] = useState<{
    lendId: number; balance: number,
    total_value: number; utility: number;
    count_refinance: number; term: number,
    user_id: string, start_date: string, start_value: number
  } | null>(null);

  useEffect(() => {
    const seq = ++loadSeqRef.current;
    const isStale = () => seq !== loadSeqRef.current;

    const fetchData = async () => {
      showLoading();
      try {
        const { data: userData } = await axios.get('user');
        if (isStale()) return;
        setUser_id(userData.data.id);

        const hasCollect = String(collect_id ?? '').trim() !== '';
        const hasOffice = String(office_id ?? '').trim() !== '';

        const [officesSettled, lendsSettled, collectsOfficeSettled] = await Promise.allSettled([
          axios.get('offices'),
          hasCollect ? axios.get(`lendcollect/${collect_id}`) : Promise.resolve(null),
          hasOffice ? axios.get(`collectsoffice/${office_id}`) : Promise.resolve(null),
        ]);

        if (isStale()) return;

        if (officesSettled.status === 'fulfilled' && officesSettled.value?.data?.data) {
          setOffices(officesSettled.value.data.data);
        } else if (officesSettled.status === 'rejected') {
          console.error('Error cargando oficinas:', officesSettled.reason);
        }

        if (collectsOfficeSettled.status === 'fulfilled' && collectsOfficeSettled.value?.data != null) {
          const raw = collectsOfficeSettled.value.data;
          setCollectsOffice(Array.isArray(raw) ? raw : []);
        } else if (collectsOfficeSettled.status === 'rejected') {
          console.error('Error cargando cobros por oficina:', collectsOfficeSettled.reason);
        }

        setSelectedIds([]);
        if (!hasCollect) {
          setLends([]);
        } else if (lendsSettled.status === 'fulfilled' && lendsSettled.value) {
          const res = lendsSettled.value as { data?: { data?: unknown } };
          const list = res.data?.data;
          const listArray = Array.isArray(list) ? (list as Lend[]) : [];
          setLends(listArray);
          if (listArray.length > 0) {
            showAlert('Se ha realizado la consulta exitosamente!', 'info');
          } else {
            showAlert('No se han encontrado registros!', 'warning');
          }
        } else {
          setLends([]);
          const reason = lendsSettled.status === 'rejected' ? lendsSettled.reason : null;
          console.error('Error cargando préstamos del cobro:', reason);
          showAlert(
            'No se pudieron cargar los préstamos. Revise la conexión o intente de nuevo.',
            'error',
          );
        }
      } catch (error) {
        if (!isStale()) {
          console.error('Error en carga de abonos:', error);
          showAlert('Error al cargar la página. ¿Sesión vencida? Vuelva a iniciar sesión.', 'error');
        }
      } finally {
        if (!isStale()) {
          hideLoading();
        }
      }
    };

    void fetchData();
  }, [collect_id, office_id, refresh]);

  const handleOpenModal = (client: Lend) => {
    setSelectedClient(client);
    setIsModalOpen(true);
    setSaldoRestante(0)
  };

  const handleOpenHistoryModal = (client: Lend) => {
    setSelectedHistoryClient({
      id: client.client?.id || 0,
      collectId: client.collect?.id || 0,
      collect: client.collect?.name || '',
      cedula: client.client?.idnumber || '',
      nombre: client.client?.fullname || '',
      direccion: client.client?.mainaddress || '',
      telefono: client.client?.phone || '',
      oficio: client.client?.job || '',
      celular: client.client?.cellphone || '',
      backup_alert_active: Boolean(client.client?.backup_alert_active),
      backups: client.client?.backups || [],
    });
    setIsHistoryModalOpen(true);
  };

  const handleOpenRefinanceModal = (client: Lend, row: any) => {
    setSelecRefinanClient(client);
    setModalData({
      lendId: row.id,
      total_value: row.total_value,
      utility: row.constant_utility,
      term: row.days_term,
      count_refinance: row.count_refinance,
      start_date: row.date,
      start_value: row.total_value,
      user_id,
      balance: row.balance,
    });
    setIsModalRefinaOpen(true);
  };

  const handleCloseUpdateModal = () => {
    setIsModalUpdateOpen(false);
    setDateClient(null);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedClient(null);
    setDateClient(null);
  };

  const handleCloseHistoryModal = () => {
    setIsHistoryModalOpen(false);
    setSelectedHistoryClient(null);
  };

  const handleCloseRefinanceModal = () => {
    setIsModalRefinaOpen(false);
    setSelecRefinanClient(null);
    setDateClient(null);
    setModalData(null);
  };

  const handleRefresh = () => {
    setRefresh(prev => !prev);
  };

  const handleDeleteSelected = async (idToDelete?: number) => {
    const idsToDelete = idToDelete ? [idToDelete] : selectedIds;

    if (idsToDelete.length === 0) {
      showAlert("Debe seleccionar al menos un préstamo para eliminar.", "warning");
      return;
    }

    if (!window.confirm(`¿Está seguro de eliminar ${idsToDelete.length} préstamo(s)? Esta acción no se puede deshacer.`)) {
      return;
    }

    showLoading();
    try {
      await axios.post('/lends/delete', { ids: idsToDelete });
      showAlert("Préstamo(s) eliminado(s) correctamente", "success");
      setSelectedIds((prev) => prev.filter((id) => !idsToDelete.includes(id)));
      handleRefresh();
    } catch (error) {
      showAlert("Ocurrió un error al eliminar", "error");
      console.error(error);
    } finally {
      hideLoading();
    }
  };

  const handleSelectionChange = (ids: RowId[]) => {
    setSelectedIds(ids.filter((id) => id !== TOTAL_ROW_ID));
  };

  // Define las columnas para SimpleCustomTable
  const simpleColumns: SimpleColumn[] = [
    { field: 'positionOrdinal', headerName: 'Pos.', width: '50px' },
    { field: 'idnumber', headerName: 'Nit', width: '100px' },
    {
      field: 'fullname',
      headerName: 'Cliente',
      width: '220px',
      maxWidth: '220px',
      wrapText: true,
      renderCell: ({ row }: { row: any }) => row.id === TOTAL_ROW_ID ? <strong>TOTAL</strong> : row.fullname,
    },
    {
      field: 'total_value',
      headerName: 'Valor Préstamo',
      width: '70px',
      align: 'right',
      renderCell: ({ row }: { row: any }) => {
        const formattedValue = formatCurrency(Number(row.total_value ?? 0));
        return row.id === TOTAL_ROW_ID ? <strong>{formattedValue}</strong> : formattedValue;
      },
    },
    { field: 'utility', headerName: 'Interés', width: '90px' },
    { field: 'date', headerName: 'Fecha', width: '85px' },
    { field: 'hour', headerName: 'Hora', width: '70px' },
    {
      field: 'balance',
      headerName: 'Saldo',
      width: '70px',
      align: 'right',
      renderCell: ({ row }: { row: any }) => {
        const formattedValue = formatCurrency(Number(row.balance ?? 0));
        return row.id === TOTAL_ROW_ID ? <strong>{formattedValue}</strong> : formattedValue;
      },
    },
    {
      field: 'days_late',
      headerName: 'Días Atraso',
      width: '60px',
      renderCell: ({ row }: { row: any }) => {
        if (row.id === TOTAL_ROW_ID) return '';
        const days = row.days_late || 0;
        let backgroundColor = '';
        if (days === 0) backgroundColor = 'transparent';
        else if (days <= 10) backgroundColor = 'transparent';
        else if (days <= 20) backgroundColor = 'yellow';
        else if (days <= 30) backgroundColor = 'blue';
        else if (days <= 40) backgroundColor = 'green';
        else backgroundColor = 'purple';

        const textColor = backgroundColor === 'transparent' || backgroundColor === 'yellow' ? 'black' : 'white';

        return (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%'
          }}>
            <div style={{
              backgroundColor,
              color: textColor,
              padding: '0.2em',
              borderRadius: '50%',
              minWidth: '2em',
              minHeight: '2em',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              textAlign: 'center'
            }}>
              {days}
            </div>
          </div>
        );
      },
    },
    {
      field: 'pay',
      headerName: 'Valor Abono',
      width: '70px',
      align: 'right',
      renderCell: ({ row }: { row: any }) => {
        if (row.id === TOTAL_ROW_ID) {
          return <strong>{formatCurrency(Number(row.pay ?? 0))}</strong>;
        }
        const pays: number = row.pay || 0;
        // Definir colores según condiciones
        let backgroundColor = '';
        if (pays === 0) backgroundColor = '#d32f2f'; // rojo (error)
        else if (pays > 0) backgroundColor = '#1976d2'; // azul (info)
        // puedes seguir agregando más rangos

        // Texto siempre legible
        const textColor = backgroundColor === '#d32f2f' ? 'white' : 'white';
        return (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%'
          }}>
            <div style={{
              backgroundColor,
              color: textColor,
              padding: '0.2em',
              borderRadius: '50%',
              minWidth: '2em',
              minHeight: '2em',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              textAlign: 'center'
            }}>
              {pays}
            </div>
          </div>
        );
      },
    },
    {
      field: 'opt_history',
      headerName: '',
      ...iconActionColumnSizing(isMobile),
      renderCell: ({ row }: { row: any }) => {
        if (row.id === TOTAL_ROW_ID) return '';
        return (
          <PermissionButton
            allowed
            deniedTooltip=""
            tooltip="Historial"
            variant="contained"
            color="primary"
            onClick={() => handleOpenHistoryModal(row)}
            sx={{ ...simpleTableIconButtonSx, mx: 'auto' }}
          >
            <Tooltip title="Historial">
              <HistoryIcon fontSize="small" />
            </Tooltip>
          </PermissionButton>
        );
      },
    },
    {
      field: 'opt_map',
      headerName: '',
      ...iconActionColumnSizing(isMobile),
      renderCell: ({ row }: { row: any }) => {
        if (row.id === TOTAL_ROW_ID) return '';
        return (
          <PermissionButton
            allowed={canShowLocation}
            deniedTooltip="Sin permiso para mostrar ubicación"
            tooltip="Ver ubicación en Google Maps"
            variant="contained"
            color="primary"
            component="a"
            href={`https://www.google.com/maps?q=${row.location}`}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ ...simpleTableIconButtonSx, mx: 'auto' }}
          >
            <Tooltip title="Ver ubicación en Google Maps">
              <EditLocationAltIcon fontSize="small" />
            </Tooltip>
          </PermissionButton>
        );
      },
    },
    {
      field: 'opt_constant',
      headerName: '',
      ...iconActionColumnSizing(isMobile),
      renderCell: ({ row }: { row: any }) => {
        if (row.id === TOTAL_ROW_ID) return '';
        return (
          <PermissionButton
            allowed={canUpdateConstant}
            deniedTooltip="Sin permiso para modificar constante"
            tooltip="Modificar constante"
            variant="contained"
            color="primary"
            onClick={() => {
              setSelectedClient(row);
              setIsModalConstantOpen(true);
            }}
            sx={{ ...simpleTableIconButtonSx, mx: 'auto' }}
          >
            <Tooltip title="Asignar Constante">
              <EditIcon fontSize="small" />
            </Tooltip>
          </PermissionButton>
        );
      },
    },
    {
      field: 'opt_pay',
      headerName: '',
      ...iconActionColumnSizing(isMobile),
      renderCell: ({ row }: { row: any }) => {
        if (row.id === TOTAL_ROW_ID) return '';
        return (
          <PermissionButton
            allowed={canPay}
            deniedTooltip="Sin permiso para registrar abonos"
            tooltip="Ingresar abono"
            variant="contained"
            color="primary"
            onClick={() => handleOpenModal(row)}
            sx={{ ...simpleTableIconButtonSx, mx: 'auto' }}
          >
            <Tooltip title="Ingresar Abono">
              <PaidIcon fontSize="small" />
            </Tooltip>
          </PermissionButton>
        );
      },
    },
    {
      field: 'opt_refinance',
      headerName: '',
      ...iconActionColumnSizing(isMobile),
      renderCell: ({ row }: { row: any }) => {
        if (row.id === TOTAL_ROW_ID) return '';
        return (
          <PermissionButton
            allowed={canRefinance}
            deniedTooltip="Sin permiso para refinanciar"
            tooltip="Refinanciar"
            variant="contained"
            color="primary"
            onClick={() => handleOpenRefinanceModal(row, row)}
            sx={{ ...simpleTableIconButtonSx, mx: 'auto' }}
          >
            <Tooltip title="Refinanciar">
              <AssessmentIcon fontSize="small" />
            </Tooltip>
          </PermissionButton>
        );
      },
    },
    {
      field: 'opt_delete',
      headerName: '',
      ...iconActionColumnSizing(isMobile),
      renderCell: ({ row }: { row: any }) => {
        if (row.id === TOTAL_ROW_ID) return '';
        return (
          <PermissionButton
            allowed={canDelete}
            deniedTooltip="Sin permiso para eliminar préstamos"
            tooltip="Eliminar"
            variant="contained"
            color="error"
            onClick={() => handleDeleteSelected(row.id)}
            sx={{ ...simpleTableIconButtonSx, mx: 'auto' }}
          >
            <Tooltip title="Eliminar">
              <DeleteIcon fontSize="small" />
            </Tooltip>
          </PermissionButton>
        );
      },
    },
  ];

  const rows = useMemo(() => {
    if (!Array.isArray(lends)) return [];
    const ordered = [...lends].sort((a, b) => {
      const pa = a.position != null ? Number(a.position) : Number.POSITIVE_INFINITY;
      const pb = b.position != null ? Number(b.position) : Number.POSITIVE_INFINITY;
      return pa - pb;
    });
    const mappedRows = ordered.map((lend, index) => {
      const client = lend.client;
      const constant = lend.constant;
      const coll = lend.collect;
      const officeId = coll?.office?.id ?? '';
      const collectPart = coll?.id ?? '';
      const paymentsForDate = lend.paymentsForDate ?? { lastPaymentTime: '00:00:00', totalValue: '' };
      return {
        ...lend,
        idconcat: `${officeId}-${collectPart}-${lend.id}`,
        id: lend.id,
        position: lend.position,
        positionOrdinal: index + 1,
        idnumber: client?.idnumber ?? '',
        fullname: client?.fullname ?? '—',
        value: lend.value,
        daily_pay: lend.daily_pay_value,
        total_value: lend.total_value,
        utility: `${lend.utility ?? ''}% ${constant?.name ?? ''}`,
        contant_id: constant?.id,
        contant: constant?.name ?? '',
        term: constant?.term,
        count_refinance: lend.count_refinance,
        constant_utility: constant?.utility,
        date: lend.date,
        hour: paymentsForDate.lastPaymentTime || '00:00:00',
        days_term: lend.days_term,
        days_late: lend.days_late,
        balance: lend.balance,
        pay: Number(paymentsForDate.totalValue ?? 0),
        state: lend.state,
      };
    });

    // Calcular los totales a mostrar en la fila resumen
    const totalValue = mappedRows.reduce((sum, row) => sum + (Number(row.total_value) || 0), 0);
    const totalBalance = mappedRows.reduce((sum, row) => sum + (Number(row.balance) || 0), 0);
    const totalPay = mappedRows.reduce((sum, row) => sum + (Number(row.pay) || 0), 0);

    // Agregar fila de total
    if (mappedRows.length > 0) {
      const totalRow: any = {
        id: TOTAL_ROW_ID,
        idconcat: 'total',
        position: '',
        positionOrdinal: '',
        idnumber: '',
        fullname: 'TOTAL',
        value: '',
        daily_pay: '',
        total_value: totalValue,
        utility: '',
        date: '',
        hour: '',
        balance: totalBalance,
        // Campos adicionales con valores por defecto
        contant_id: 0,
        contant: '',
        term: 0,
        count_refinance: 0,
        constant_utility: 0,
        days_term: 0,
        days_late: 0,
        pay: totalPay,
        state: 0,
        disableSelection: true,
      };
      mappedRows.push(totalRow);
    }

    return mappedRows;
  }, [lends]);

  return (
    <HeaderResponsive allowHorizontalOverflow sx={{ pb: { xs: 10, md: 8 } }}>
      <StickyListToolbar>
        <Title>Administrar Abonos</Title>
        <Box
          sx={{
            ml: 'auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            flexWrap: 'wrap',
            gap: isMobile ? 1 : 2,
          }}
        >
          <PermissionButton
            allowed={canCreate}
            deniedTooltip="Sin permiso para agregar préstamos"
            tooltip="Agregar"
            variant="contained"
            size="small"
            startIcon={isMobile ? null : <AddIcon />}
            component={NavLink}
            to="/prestamos/abonos/add"
          >
            {isMobile ? <AddIcon /> : "Agregar"}
          </PermissionButton>
          <PermissionButton
            allowed={canCreateClient}
            deniedTooltip="Sin permiso para crear clientes"
            tooltip="Agregar Cliente"
            variant="contained"
            size="small"
            startIcon={isMobile ? null : <AddIcon />}
            component={NavLink}
            to="/administrar/clientes/add"
            sx={{ backgroundColor: "black" }}
          >
            {isMobile ? <PersonAddAltIcon /> : "Agregar Cliente"}
          </PermissionButton>
          <PermissionButton
            allowed={canDeleteSelected}
            deniedTooltip="Sin permiso para eliminar seleccionados"
            tooltip="Eliminar seleccionados"
            variant="contained"
            size="small"
            color="error"
            startIcon={isMobile ? null : <DeleteIcon />}
            onClick={() => handleDeleteSelected()}
          >
            {isMobile ? <DeleteIcon /> : "Eliminar"}
          </PermissionButton>
        </Box>
      </StickyListToolbar>
      <Card
        sx={{
          width: (theme) => stickyListRegionWidth(theme, drawerOpen),
          maxWidth: '100%',
          boxSizing: 'border-box',
          overflowX: 'auto',
        }}
      >
        <Box sx={{ pb: 0.5 }}>
          <CollectByOfficeForm
            offices={offices}
            collectsOffice={collectsOffice}
          />
        </Box>
        <CardContent
          sx={{
            width: (theme) => stickyListRegionWidth(theme, drawerOpen),
            maxWidth: '100%',
            boxSizing: 'border-box',
            overflowX: 'auto',
            pt: 0.5,
            pb: 4,
          }}
        >

          {lends.length > 0 ? (
            <SimpleCustomTable
              rows={rows}
              columns={simpleColumns}
              checkboxSelection
              selectionModel={selectedIds}
              onRowSelectionModelChange={handleSelectionChange}
              countValue={lends.length}
              disableInternalScroll
              rowHeight={24}
              exportCsv
              exportFileName="abonos.csv"
              wrapCellContent
              columnGroups={[{ header: 'Opciones', fields: ['opt_history', 'opt_delete', 'opt_refinance', 'opt_pay', 'opt_constant', 'opt_map'] }]}
              getRowCellSx={({ row }) =>
                row.id === TOTAL_ROW_ID
                  ? {
                      backgroundColor: '#ffd3b8',
                      fontWeight: 700,
                    }
                  : undefined
              }
            />
          ) : (
            <EmptyStateMessage message="No existen registros" align="left" />
          )}

          {/* Modales */}
      <PaysModalWeb
        open={isModalOpen}
        onClose={handleCloseModal}
        client={selectedClient}
        clientId={selectedClient?.id}
        canAllowDiscount={canAllowDiscount}
        canAllowManualDiscount={canAllowManualDiscount}
        canAllowAutomaticDiscount={canAllowAutomaticDiscount}
        canPdf={canPdf}
        showAlert={showAlert}
        showLoading={showLoading}
            hideLoading={hideLoading}
            onPaymentSuccess={handleRefresh}
          />
          <ConstantModal
            open={isModalConstantOpen}
            onClose={() => setIsModalConstantOpen(false)}
            client={selectedClient}
            officeId={office_id}
            collectId={collect_id}
            userId={user_id}
            showAlert={showAlert}
            showLoading={showLoading}
            hideLoading={hideLoading}
            onPaymentSuccess={() => {
              setIsModalConstantOpen(false);
              handleRefresh();
            }}
          />
          <RefinanceModal
            open={isModalRefinanceOpen}
            onClose={handleCloseRefinanceModal}
            client={selectedRefinanceClient}
            clientId={selectedRefinanceClient?.id}
            modalData={modalData}
            showAlert={showAlert}
            showLoading={showLoading}
            hideLoading={hideLoading}
            onPaymentSuccess={handleRefresh}
          />
          <UpdateDateModal
            open={isModalUpdateOpen}
            onClose={handleCloseUpdateModal}
            client={dateClient}
            clientId={dateClient?.id}
            showAlert={showAlert}
            showLoading={showLoading}
            hideLoading={hideLoading}
          />
          <ModalClientHistory
            open={isHistoryModalOpen}
            onClose={handleCloseHistoryModal}
            clientData={selectedHistoryClient}
            showAlert={showAlert}
            showLoading={showLoading}
            hideLoading={hideLoading}
          />
        </CardContent>
      </Card>
      <Box sx={{ height: { xs: 48, md: 32 } }} />
    </HeaderResponsive>
  );
}

export default Abonos;
